import { ArrowRight, ChevronDown, Sparkles, Database, Zap, Shield } from "lucide-react";
import FirstShowImage from "../assets/FirstShowImage.png";

export function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 px-6 overflow-hidden">
      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* Badge */}
        <div 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-medium text-emerald-400">Early Access — 100% Free</span>
          <Sparkles className="w-4 h-4 text-emerald-400" />
        </div>

        {/* Main Headline */}
        <h1 
          className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight mb-6 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
        >
          <span className="text-white">The Database Manager</span>
          <br />
          <span className="gradient-text">You Deserve</span>
        </h1>

        {/* Subheadline */}
        <p 
          className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed opacity-0 animate-fade-in-up"
          style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
        >
          Connect to MySQL, PostgreSQL, MongoDB, SQLite, Redis, Oracle, and NeonDB from one powerful, 
          beautifully designed interface. Query, visualize, and manage your data with ease.
        </p>

        {/* Feature Pills */}
        <div 
          className="flex flex-wrap items-center justify-center gap-3 mb-10 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '350ms', animationFillMode: 'forwards' }}
        >
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400">
            <Database className="w-3.5 h-3.5 text-emerald-400" />
            <span>7 Database Engines</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400">
            <Zap className="w-3.5 h-3.5 text-emerald-400" />
            <span>Lightning Fast</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400">
            <Shield className="w-3.5 h-3.5 text-emerald-400" />
            <span>Secure & Encrypted</span>
          </div>
        </div>

        {/* CTA Button */}
        <div 
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
        >
          <a
            href="#download"
            className="group px-10 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/30 flex items-center gap-2 hover:-translate-y-1"
          >
            Download for Free
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#features"
            className="px-8 py-4 text-zinc-400 hover:text-white font-medium transition-colors flex items-center gap-2"
          >
            Explore Features
            <ChevronDown className="w-4 h-4" />
          </a>
        </div>

        {/* Stats Row */}
        <div 
          className="grid grid-cols-3 gap-8 max-w-md mx-auto mb-16 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '450ms', animationFillMode: 'forwards' }}
        >
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-white mb-1">7</div>
            <div className="text-xs sm:text-sm text-zinc-500">Databases</div>
          </div>
          <div className="text-center border-x border-white/10">
            <div className="text-2xl sm:text-3xl font-bold text-white mb-1">∞</div>
            <div className="text-xs sm:text-sm text-zinc-500">Connections</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-white mb-1">3</div>
            <div className="text-xs sm:text-sm text-zinc-500">Platforms</div>
          </div>
        </div>

        {/* App Preview Mockup */}
        <div 
          className="relative mockup-glow opacity-0 animate-scale-in"
          style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}
        >
          <div className="relative bg-zinc-900/80 rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
            {/* Window Controls */}
            <div className="flex items-center gap-2 px-4 py-3 bg-zinc-900/50 border-b border-white/5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-4 text-xs text-zinc-500 font-mono">Nexatice — Data Browser</span>
            </div>
            
            {/* App screenshot */}
            <img 
              src={FirstShowImage} 
              alt="Nexatice Data Browser - View and manage your database tables"
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator">
          <ChevronDown className="w-6 h-6 text-zinc-500" />
        </div>
      </div>
    </section>
  );
}
